import { useEffect, useRef } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * @param {Object} options - Intersection Observer options
 * @returns {ref} - React ref to attach to the element
 */
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Optionally unobserve after animation triggers
          // observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: options.threshold || 0.1,
      rootMargin: options.rootMargin || '0px',
      ...options
    });

    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options]);

  return ref;
};

/**
 * Custom hook for animating multiple items with stagger effect
 * @param {Object} options - Intersection Observer options
 * @returns {ref} - React ref to attach to the container element
 */
export const useStaggerAnimation = (options = {}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const children = entry.target.querySelectorAll('[data-animate]');
          children.forEach((child, index) => {
            // Add base delay + stagger delay for consistent timing
            const baseDelay = options.baseDelay || 50;
            const delay = baseDelay + (index * (options.staggerDelay || 100));
            child.style.animationDelay = `${delay}ms`;
            child.classList.add('visible');
            
            // Clear the animation delay and reset for hover transitions
            setTimeout(() => {
              child.style.animationDelay = '';
              // Force hover transitions to work by removing any conflicting styles
              if (child.classList.contains('apple-smooth-hover')) {
                child.style.transition = 'transform 240ms cubic-bezier(0.28, 0.11, 0.32, 1), background-color 240ms cubic-bezier(0.28, 0.11, 0.32, 1)';
              }
            }, delay + 500);
          });
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: options.threshold || 0.1,
      rootMargin: options.rootMargin || '0px',
      ...options
    });

    const element = containerRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [options]);

  return containerRef;
};